// A tiny framework-agnostic pub/sub for "a route transition is in flight".
//
// The RBAC route gates (adminShellOnEnter / abilityOnEnter) await a /me/ability
// server call inside react-router-3's `onEnter`, which blocks the transition
// with NO built-in visual feedback — so a menu click feels dead. These helpers
// run OUTSIDE React (plain functions the router calls), so they can't use React
// state. This store lets them signal start/stop, and a small React bar component
// subscribes to render a top progress indicator.
//
// Counter-based so overlapping/parent+child onEnter calls don't stop the bar
// prematurely — it's active while any start() is unmatched by a stop().

type Listener = (active: boolean) => void

let pendingCount = 0
const listeners = new Set<Listener>()

function emit(): void {
  const active = pendingCount > 0
  listeners.forEach((listener) => listener(active))
}

export const navigationProgress = {
  start(): void {
    pendingCount += 1
    if (pendingCount === 1) {
      emit()
    }
  },
  stop(): void {
    if (pendingCount === 0) {
      return
    }
    pendingCount -= 1
    if (pendingCount === 0) {
      emit()
    }
  },
  // Force-clear — used by the router's onUpdate/onError once a transition fully
  // settles, so the bar can never get stuck if a start() lacked its stop().
  reset(): void {
    if (pendingCount === 0) {
      return
    }
    pendingCount = 0
    emit()
  },
  isActive(): boolean {
    return pendingCount > 0
  },
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}
